// src/components/orderentry/panels/ContractPanel.jsx
// -----------------------------------------------------------------------------
// AREA 5 — CONTRACT PRICING
//
// Responsibilities:
//  - Display the contract pricing rows returned from the backend
//  - Render a header row + rows in a wide horizontal-scroll container
//  - Stay quiet if customer context is not selected
// -----------------------------------------------------------------------------

import React from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import GridBadge from '../components/GridBadge.jsx';
import MoneyCell from '../components/MoneyCell.jsx';

export default function ContractPanel({
  masId,
  shipId,
  rowHeight,
  contractRows, // array of rows
}) {
  if (!masId || !shipId) {
    return null;
  }

  const rows = Array.isArray(contractRows) ? contractRows : [];

  return (
    <Box sx={{ position: 'relative', minWidth: 0 }}>
      <GridBadge n="5" />

      {/* Keep title space; you can add actual text later */}
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {/* Contract Pricing */}
      </Typography>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          px: 1,
          py: 0.5,
          minWidth: 0,
          overflowX: 'auto',
          minHeight: rowHeight * 1.2,
          maxHeight: rowHeight * 5,
          overflowY: 'auto',
        }}
      >
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No contracts found for this customer/ship-to + item.
          </Typography>
        )}

        {rows.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '60px 180px 70px 90px 90px 90px 90px 90px 120px',
              gap: 1,
              alignItems: 'center',
              minWidth: 960,
            }}
          >
            {/* Header row */}
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Code
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Contract
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Tier
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Freight
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              P1
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              P2
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              P3
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              P4
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Terms
            </Typography>

            {/* Data rows */}
            {rows.map((c) => (
              <React.Fragment
                key={`${c.contractNo || ''}-${c.contractCode || ''}-${String(c.tier ?? '')}-${String(c.price1 ?? '')}-${String(c.price2 ?? '')}-${String(c.price3 ?? '')}-${String(c.price4 ?? '')}-${String(c.termsCode ?? '')}`}
              >
                <Typography variant="body2">{c.contractCode || '—'}</Typography>
                <Typography variant="body2">{c.contractNo || '—'}</Typography>
                <Typography variant="body2">{c.tier != null ? `Tier ${c.tier}` : '—'}</Typography>
                <Typography variant="body2">{c.freeFreight || '—'}</Typography>
                <Typography variant="body2">
                  <MoneyCell value={c.price1} />
                </Typography>
                <Typography variant="body2">
                  <MoneyCell value={c.price2} />
                </Typography>
                <Typography variant="body2">
                  <MoneyCell value={c.price3} />
                </Typography>
                <Typography variant="body2">
                  <MoneyCell value={c.price4} />
                </Typography>
                <Typography variant="body2">{c.termsDescription || c.termsCode || '—'}</Typography>
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
